import { Controller } from "@hotwired/stimulus"

const ORDER_SUBJECTS = [
  "order_where",
  "order_cancellation",
  "order_damaged",
  "order_return_and_refund"
]

export default class extends Controller {
  static targets = [
    "attachment",
    "extensionError",
    "fileInput",
    "orderData",
    "orderDropdown",
    "orderDropdownButton",
    "orderInput",
    "orderOption",
    "orderSelection",
    "panel",
    "panelTrigger",
    "saleData",
    "saleInput",
    "sizeError",
    "subject"
  ]

  static values = {
    orderPrompt: String,
    submitted: Boolean
  }

  initialize() {
    this.closeOrderDropdownFromOutside = this.closeOrderDropdownFromOutside.bind(this)
    this.closeOrderDropdownFromEscape = this.closeOrderDropdownFromEscape.bind(this)
  }

  connect() {
    this.updatePanels()
    this.updateSubjectFields(false)
    this.restoreOrderSelection()
    document.addEventListener("click", this.closeOrderDropdownFromOutside)
    document.addEventListener("keydown", this.closeOrderDropdownFromEscape)

    if (this.submittedValue) {
      requestAnimationFrame(() => document.getElementById("contact-email")?.scrollIntoView({ behavior: "smooth" }))
    }
  }

  disconnect() {
    document.removeEventListener("click", this.closeOrderDropdownFromOutside)
    document.removeEventListener("keydown", this.closeOrderDropdownFromEscape)
    if (this.fileErrorTimeout) clearTimeout(this.fileErrorTimeout)
  }

  togglePanel(event) {
    const panel = document.getElementById(event.currentTarget.getAttribute("aria-controls"))
    if (!panel) return

    const open = !panel.classList.contains("open")

    this.panelTargets.forEach(otherPanel => otherPanel.classList.toggle("open", otherPanel === panel && open))
    this.updatePanels()
  }

  subjectChanged() {
    this.updateSubjectFields(true)
  }

  toggleOrderDropdown(event) {
    event.preventDefault()
    event.stopPropagation()

    const open = !this.orderDropdownTarget.classList.contains("open")
    this.setOrderDropdownOpen(open)
  }

  selectOrder(event) {
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return

    event.preventDefault()
    this.selectOrderOption(event.currentTarget)
    this.setOrderDropdownOpen(false)
  }

  validateFiles() {
    const allowedExtensions = ["jpg", "jpeg", "png", "zip", "pdf"]
    const maximumFileSize = Number.parseInt(this.fileInputTarget.dataset.maxFileSize, 10)
    const files = Array.from(this.fileInputTarget.files)
    const sizeExceeded = files.some(file => maximumFileSize && file.size > maximumFileSize)
    const extensionError = files.some(file => !allowedExtensions.includes(file.name.split(".").pop().toLowerCase()))

    this.hideFileErrors()
    if (!sizeExceeded && !extensionError) return

    this.fileInputTarget.value = ""
    this.attachmentTarget.classList.add("error")
    this.sizeErrorTarget.hidden = !sizeExceeded
    this.extensionErrorTarget.hidden = !extensionError
    this.fileErrorTimeout = setTimeout(() => this.hideFileErrors(), 5000)
  }

  updatePanels() {
    this.panelTriggerTargets.forEach(trigger => {
      const panel = document.getElementById(trigger.getAttribute("aria-controls"))
      trigger.setAttribute("aria-expanded", panel?.classList.contains("open").toString() || "false")
    })
  }

  updateSubjectFields(clearHiddenFields) {
    if (!this.hasSubjectTarget) return

    const displayOrder = ORDER_SUBJECTS.includes(this.subjectTarget.value)
    const displaySale = this.subjectTarget.value === "product_info"

    if (this.hasOrderDataTarget) this.orderDataTarget.hidden = !displayOrder
    if (this.hasSaleDataTarget) this.saleDataTarget.hidden = !displaySale

    if (clearHiddenFields && !displayOrder) this.clearOrder()
    if (clearHiddenFields && !displaySale && this.hasSaleInputTarget) this.saleInputTarget.value = ""
  }

  restoreOrderSelection() {
    if (!this.hasOrderInputTarget || !this.orderInputTarget.value || !this.hasOrderSelectionTarget) return

    const option = this.orderOptionTargets.find(orderOption => orderOption.dataset.id === this.orderInputTarget.value)
    if (option) this.selectOrderOption(option)
  }

  selectOrderOption(option) {
    this.orderInputTarget.value = option.dataset.id
    this.orderSelectionTarget.innerHTML = option.innerHTML
  }

  clearOrder() {
    if (this.hasOrderInputTarget) this.orderInputTarget.value = ""
    if (this.hasOrderSelectionTarget) this.orderSelectionTarget.textContent = this.orderPromptValue
  }

  setOrderDropdownOpen(open) {
    if (!this.hasOrderDropdownTarget) return

    this.orderDropdownTarget.classList.toggle("open", open)
    this.orderDropdownButtonTarget.setAttribute("aria-expanded", open.toString())
  }

  closeOrderDropdownFromOutside(event) {
    if (this.hasOrderDropdownTarget && !this.orderDropdownTarget.contains(event.target)) {
      this.setOrderDropdownOpen(false)
    }
  }

  closeOrderDropdownFromEscape(event) {
    if (event.key === "Escape") this.setOrderDropdownOpen(false)
  }

  hideFileErrors() {
    if (!this.hasAttachmentTarget) return

    if (this.fileErrorTimeout) clearTimeout(this.fileErrorTimeout)
    this.fileErrorTimeout = null
    this.attachmentTarget.classList.remove("error")
    this.sizeErrorTarget.hidden = true
    this.extensionErrorTarget.hidden = true
  }
}
